import React, { useState, useEffect, useCallback, useRef } from 'react';
import MessageRenderer from './MessageRenderer';
import WebSearchProgress from './WebSearchProgress';
import ThinkingDisplay from './ThinkingDisplay';
import MCPToolProgress from './MCPToolProgress';
import ChartDisplay from './ChartDisplay';
import { Bot, Loader2, Zap, Globe, Code, Eye, Maximize2, Download, Copy } from 'lucide-react';

const StreamingMessageRenderer = React.memo(({ content, streamingTokens = [], isStreaming = false, model }) => {
  const [messageSegments, setMessageSegments] = useState([]);
  const [currentContent, setCurrentContent] = useState('');
  const [webSearchActive, setWebSearchActive] = useState(false);
  const [webSearchQuery, setWebSearchQuery] = useState('');
  const [thinkingActive, setThinkingActive] = useState(false);
  const [thinkingContent, setThinkingContent] = useState('');
  const [progressInfo, setProgressInfo] = useState(null);
  const [htmlViewMode, setHtmlViewMode] = useState('preview'); // 'preview' or 'code'
  const [isFullscreen, setIsFullscreen] = useState(false);
  const updateTimeoutRef = useRef(null);
  const htmlUpdateTimeoutRef = useRef(null);

  // Function to detect if content contains HTML code blocks
  const isHtmlContent = (text) => {
    if (!text || typeof text !== 'string') return false;
    
    // Look for ```html code blocks
    const htmlCodeBlockPattern = /```html\s*([\s\S]*?)```/i;
    return htmlCodeBlockPattern.test(text);
  };

  // Extract HTML content from code blocks
  const extractHtmlFromCodeBlock = (text) => {
    const htmlCodeBlockPattern = /```html\s*([\s\S]*?)```/i;
    const match = text.match(htmlCodeBlockPattern);
    if (match) {
      // Unescape any HTML entities that might have been escaped
      return match[1].trim()
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
    }
    return text;
  };

  const processTokens = useCallback(() => {
    // If we have content with embedded tokens (from database or after streaming), parse them
    if (!isStreaming && content && (streamingTokens.length === 0 || content.includes('MCP_TOOL_') || content.includes('WEB_SEARCH_') || content.includes('CHART_') || content.includes('COMPACTION_SUMMARY'))) {
      // Split content by MCP token, WEB_SEARCH token, CHART token, and COMPACTION_SUMMARY boundaries - match with or without underscores
      const tokenPattern = /_*(MCP_TOOL_(START|EXECUTING|RESULT|ERROR)|WEB_SEARCH_(RESULTS_START|RESULT)|CHART_(START|END)|COMPACTION_SUMMARY)_*/g;
      const extractedTokens = [];
      let lastIndex = 0;
      let match;

      while ((match = tokenPattern.exec(content)) !== null) {
        const tokenStart = match.index;
        const fullTokenType = match[1]; // Full match like "MCP_TOOL_START" or "WEB_SEARCH_RESULT"

        // Add text before the token as regular content
        if (tokenStart > lastIndex) {
          const textBefore = content.substring(lastIndex, tokenStart);
          if (textBefore.trim()) {
            extractedTokens.push(textBefore);
          }
        }

        // Extract JSON data if present
        const afterToken = content.substring(match.index + match[0].length);
        let jsonData = '';
        let tokenEnd = match.index + match[0].length;

        if (afterToken.startsWith('{')) {
          // Extract JSON by counting braces
          let braceCount = 0;
          for (let i = 0; i < afterToken.length; i++) {
            if (afterToken[i] === '{') braceCount++;
            if (afterToken[i] === '}') braceCount--;
            if (braceCount === 0 && afterToken[i] === '}') {
              jsonData = afterToken.substring(0, i + 1);
              tokenEnd = match.index + match[0].length + i + 1;
              break;
            }
          }
        }

        // Add the complete token with normalized format (preserve full token type)
        extractedTokens.push(`__${fullTokenType}__${jsonData}`);
        lastIndex = tokenEnd;
      }

      // Add remaining text after last token
      if (lastIndex < content.length) {
        const textAfter = content.substring(lastIndex);
        if (textAfter.trim()) {
          extractedTokens.push(textAfter);
        }
      }

      // If we extracted tokens, process them as streaming tokens
      if (extractedTokens.length > 0) {
        // Process as if they were streaming tokens
        const segments = [];
        let tempContent = '';
        let mcpToolStartIndex = -1;

        extractedTokens.forEach((token) => {
          if (token.startsWith('__MCP_TOOL_START__')) {
            if (tempContent) {
              segments.push({ type: 'content', content: tempContent });
              tempContent = '';
            }
            try {
              const data = JSON.parse(token.replace('__MCP_TOOL_START__', ''));
              mcpToolStartIndex = segments.length;
              segments.push({
                type: 'mcp_tool',
                toolName: data.toolName,
                serverName: data.serverName,
                input: data.input,
                status: 'starting',
                result: null,
                error: null
              });
            } catch (e) {
              console.error('Error parsing MCP_TOOL_START:', e);
            }
          } else if (token.startsWith('__MCP_TOOL_EXECUTING__')) {
            try {
              const jsonStr = token.replace('__MCP_TOOL_EXECUTING__', '');
              // If there's JSON data, parse it and update with tool details
              if (jsonStr.trim().startsWith('{')) {
                const data = JSON.parse(jsonStr);
                // Find the most recent MCP tool that's in 'starting' status
                for (let i = segments.length - 1; i >= 0; i--) {
                  if (segments[i].type === 'mcp_tool' && segments[i].status === 'starting') {
                    segments[i].toolName = data.toolName || segments[i].toolName;
                    segments[i].serverName = data.serverName || segments[i].serverName;
                    segments[i].input = data.input || segments[i].input;
                    segments[i].status = 'executing';
                    break;
                  }
                }
              } else {
                // Fallback to old behavior
                for (let i = segments.length - 1; i >= 0; i--) {
                  if (segments[i].type === 'mcp_tool' && segments[i].status === 'starting') {
                    segments[i].status = 'executing';
                    break;
                  }
                }
              }
            } catch (e) {
              console.error('Error parsing MCP_TOOL_EXECUTING:', e);
              // Fallback to old behavior
              for (let i = segments.length - 1; i >= 0; i--) {
                if (segments[i].type === 'mcp_tool' && segments[i].status === 'starting') {
                  segments[i].status = 'executing';
                  break;
                }
              }
            }
          } else if (token.startsWith('__MCP_TOOL_RESULT__')) {
            try {
              const data = JSON.parse(token.replace('__MCP_TOOL_RESULT__', ''));
              // Find the most recent MCP tool that's in 'starting' or 'executing' status
              for (let i = segments.length - 1; i >= 0; i--) {
                if (segments[i].type === 'mcp_tool' && (segments[i].status === 'starting' || segments[i].status === 'executing')) {
                  segments[i].status = 'completed';
                  segments[i].result = data.result;
                  break;
                }
              }
            } catch (e) {
              console.error('Error parsing MCP_TOOL_RESULT:', e);
            }
          } else if (token.startsWith('__MCP_TOOL_ERROR__')) {
            try {
              const data = JSON.parse(token.replace('__MCP_TOOL_ERROR__', ''));
              // Find the most recent MCP tool that's in 'starting' or 'executing' status
              for (let i = segments.length - 1; i >= 0; i--) {
                if (segments[i].type === 'mcp_tool' && (segments[i].status === 'starting' || segments[i].status === 'executing')) {
                  segments[i].status = 'error';
                  segments[i].error = data.error;
                  break;
                }
              }
            } catch (e) {
              console.error('Error parsing MCP_TOOL_ERROR:', e);
            }
          } else if (token.startsWith('__WEB_SEARCH_RESULTS_START__')) {
            // Web search results starting
            if (tempContent) {
              segments.push({ type: 'content', content: tempContent });
              tempContent = '';
            }
            const webSearchIndex = segments.length;
            segments.push({
              type: 'web_search',
              status: 'results',
              results: []
            });
          } else if (token.startsWith('__WEB_SEARCH_RESULT__')) {
            try {
              const data = JSON.parse(token.replace('__WEB_SEARCH_RESULT__', ''));
              // Find the most recent web search segment
              for (let i = segments.length - 1; i >= 0; i--) {
                if (segments[i].type === 'web_search') {
                  if (!segments[i].results) segments[i].results = [];
                  segments[i].results.push(data);
                  break;
                }
              }
            } catch (e) {
              console.error('Error parsing WEB_SEARCH_RESULT:', e);
            }
          } else if (token.startsWith('__CHART_START__')) {
            try {
              // Extract chart data between __CHART_START__ and __CHART_END__
              const chartDataStr = token.replace('__CHART_START__', '').replace('__CHART_END__', '');
              const chartData = JSON.parse(chartDataStr);

              if (tempContent) {
                segments.push({ type: 'content', content: tempContent });
                tempContent = '';
              }

              segments.push({
                type: 'chart',
                config: chartData.config,
                metadata: chartData.metadata
              });
            } catch (e) {
              console.error('Error parsing CHART data:', e);
            }
          } else if (token.startsWith('__COMPACTION_SUMMARY__')) {
            try {
              const compactionDataStr = token.replace('__COMPACTION_SUMMARY__', '');
              const compactionData = JSON.parse(compactionDataStr);

              if (tempContent) {
                segments.push({ type: 'content', content: tempContent });
                tempContent = '';
              }

              segments.push({
                type: 'compaction_summary',
                ...compactionData
              });
            } catch (e) {
              console.error('Error parsing COMPACTION_SUMMARY data:', e);
            }
          } else if (!token.startsWith('__')) {
            tempContent += token;
          }
        });

        // Add remaining content
        if (tempContent.trim()) {
          segments.push({ type: 'content', content: tempContent.trim() });
        }

        setMessageSegments(segments);
        return;
      }

      // For completed messages without streaming tokens, split content by HTML code blocks
      if (isHtmlContent(content)) {
        const segments = [];
        let remainingContent = content;
        const htmlBlockRegex = /```html\s*([\s\S]*?)```/i;

        while (remainingContent) {
          const match = remainingContent.match(htmlBlockRegex);

          if (match) {
            // Add content before the HTML block
            if (match.index > 0) {
              const beforeContent = remainingContent.substring(0, match.index).trim();
              if (beforeContent) {
                segments.push({ type: 'content', content: beforeContent });
              }
            }

            // Add the HTML content (unescaped)
            const htmlContent = match[1].trim()
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>')
              .replace(/&amp;/g, '&')
              .replace(/&quot;/g, '"')
              .replace(/&#39;/g, "'");
            segments.push({ type: 'html_content', content: htmlContent });

            // Continue with remaining content after the HTML block
            remainingContent = remainingContent.substring(match.index + match[0].length);
          } else {
            // No more HTML blocks, add remaining content
            if (remainingContent.trim()) {
              segments.push({ type: 'content', content: remainingContent.trim() });
            }
            break;
          }
        }
        setMessageSegments(segments);
      } else {
        setMessageSegments([{ type: 'content', content }]);
      }
      return;
    }

    // Process streaming tokens with debounced updates
    const segments = [];
    let tempContent = '';
    let tempThinking = '';
    let inWebSearch = false;
    let inThinking = false;
    let currentProgress = null;
    let webSearchStartIndex = -1;
    let thinkingStartIndex = -1;
    let inHtmlBlock = false;
    let htmlStartIndex = -1;
    let accumulatedHtml = '';
    let mcpToolStartIndex = -1;
    let latestMcpToolIndex = -1;
    // Track multiple MCP tools by their segment index
    const mcpToolIndexes = [];

    streamingTokens.forEach((token, index) => {
      if (token.startsWith('__PROGRESS_START__')) {
        // Extract progress text
        const progressText = token.replace('__PROGRESS_START__', '');
        currentProgress = { status: 'starting', text: progressText };
        setProgressInfo(currentProgress);
      } else if (token.startsWith('__TOOL_AUTO__')) {
        const toolText = token.replace('__TOOL_AUTO__', '');
        currentProgress = { status: 'tool_auto', text: toolText };
        setProgressInfo(currentProgress);
      } else if (token.startsWith('__TOOLS_READY__')) {
        const toolsText = token.replace('__TOOLS_READY__', '');
        currentProgress = { status: 'tools_ready', text: toolsText };
        setProgressInfo(currentProgress);
      } else if (token.startsWith('__STREAM_START__')) {
        // Skip processing __STREAM_START__ tokens to avoid flickering
        // The message will be handled by the fallback "Generating response..." display
      } else if (token.startsWith('__WEB_SEARCH_START__')) {
        // Save any accumulated content
        if (tempContent) {
          segments.push({ type: 'content', content: tempContent });
          tempContent = '';
        }
        inWebSearch = true;
        webSearchStartIndex = segments.length;
        segments.push({ type: 'web_search', status: 'starting', query: '', results: [] });
        setWebSearchActive(true);
        setWebSearchQuery(''); // Reset global query for current search
      } else if (token.startsWith('__WEB_SEARCH_QUERY__')) {
        const query = token.replace('__WEB_SEARCH_QUERY__', '');
        setWebSearchQuery(prev => prev + query);
        // Update the current search segment's query
        if (webSearchStartIndex >= 0 && webSearchStartIndex < segments.length) {
          const currentQuery = segments[webSearchStartIndex].query || '';
          segments[webSearchStartIndex] = { 
            ...segments[webSearchStartIndex], 
            query: currentQuery + query 
          };
        }
      } else if (token.startsWith('__WEB_SEARCH_EXECUTING__')) {
        if (webSearchStartIndex >= 0 && webSearchStartIndex < segments.length) {
          segments[webSearchStartIndex] = { 
            ...segments[webSearchStartIndex], 
            status: 'searching' 
          };
        }
        inWebSearch = false;
      } else if (token.startsWith('__WEB_SEARCH_RESULTS_START__')) {
        if (webSearchStartIndex >= 0 && webSearchStartIndex < segments.length) {
          segments[webSearchStartIndex] = { 
            ...segments[webSearchStartIndex],
            status: 'results'
          };
        }
      } else if (token.startsWith('__WEB_SEARCH_RESULT__')) {
        try {
          const resultData = JSON.parse(token.replace('__WEB_SEARCH_RESULT__', ''));
          if (webSearchStartIndex >= 0 && webSearchStartIndex < segments.length && segments[webSearchStartIndex].results) {
            segments[webSearchStartIndex].results.push(resultData);
          }
        } catch (e) {
          // Silently ignore JSON parsing errors during streaming
        }
      } else if (token.includes('MCP_TOOL_START')) {
        // Save any accumulated content
        if (tempContent) {
          segments.push({ type: 'content', content: tempContent });
          tempContent = '';
        }

        try {
          // Extract JSON from token - find the JSON object after MCP_TOOL_START
          // Token format: __MCP_TOOL_START__{"toolName":...}
          const startIdx = token.indexOf('MCP_TOOL_START') + 'MCP_TOOL_START'.length;
          let jsonStr = token.substring(startIdx);

          // Skip any underscores after the token name
          jsonStr = jsonStr.replace(/^_+/, '');

          // Extract JSON by counting braces
          let braceCount = 0;
          let jsonEnd = -1;
          for (let i = 0; i < jsonStr.length; i++) {
            if (jsonStr[i] === '{') braceCount++;
            if (jsonStr[i] === '}') braceCount--;
            if (braceCount === 0 && jsonStr[i] === '}') {
              jsonEnd = i + 1;
              break;
            }
          }

          if (jsonEnd > 0) {
            const data = JSON.parse(jsonStr.substring(0, jsonEnd));
            mcpToolStartIndex = segments.length;
            latestMcpToolIndex = mcpToolStartIndex;
            // Track this tool's index for later status updates
            mcpToolIndexes.push(mcpToolStartIndex);
            segments.push({
              type: 'mcp_tool',
              toolName: data.toolName,
              serverName: data.serverName,
              input: data.input,
              status: 'starting',
              result: null,
              error: null
            });
          }
        } catch (e) {
          console.error('Error parsing MCP_TOOL_START:', e, 'Token:', token);
        }
      } else if (token.includes('MCP_TOOL_EXECUTING')) {
        try {
          const startIdx = token.indexOf('MCP_TOOL_EXECUTING') + 'MCP_TOOL_EXECUTING'.length;
          let jsonStr = token.substring(startIdx);
          // Skip any underscores after the token name
          jsonStr = jsonStr.replace(/^_+/, '');

          // If there's JSON data, parse it and update with tool details
          if (jsonStr.trim().startsWith('{')) {
            const data = JSON.parse(jsonStr);
            // Find the first MCP tool that's still in 'starting' status
            const toolIndex = mcpToolIndexes.find(idx =>
              segments[idx] && segments[idx].status === 'starting'
            );
            if (toolIndex !== undefined && toolIndex < segments.length) {
              segments[toolIndex] = {
                ...segments[toolIndex],
                toolName: data.toolName || segments[toolIndex].toolName,
                serverName: data.serverName || segments[toolIndex].serverName,
                input: data.input || segments[toolIndex].input,
                status: 'executing'
              };
            }
          } else {
            // Fallback to old behavior if no JSON
            const toolIndex = mcpToolIndexes.find(idx =>
              segments[idx] && segments[idx].status === 'starting'
            );
            if (toolIndex !== undefined && toolIndex < segments.length) {
              segments[toolIndex] = {
                ...segments[toolIndex],
                status: 'executing'
              };
            }
          }
        } catch (e) {
          console.error('Error parsing MCP_TOOL_EXECUTING:', e);
          // Fallback to old behavior
          const toolIndex = mcpToolIndexes.find(idx =>
            segments[idx] && segments[idx].status === 'starting'
          );
          if (toolIndex !== undefined && toolIndex < segments.length) {
            segments[toolIndex] = {
              ...segments[toolIndex],
              status: 'executing'
            };
          }
        }
      } else if (token.includes('MCP_TOOL_RESULT')) {
        try {
          const startIdx = token.indexOf('MCP_TOOL_RESULT') + 'MCP_TOOL_RESULT'.length;
          let jsonStr = token.substring(startIdx);
          // Skip any underscores after the token name
          jsonStr = jsonStr.replace(/^_+/, '');
          const data = JSON.parse(jsonStr);
          // Find the first MCP tool that's in 'starting' or 'executing' status
          const toolIndex = mcpToolIndexes.find(idx =>
            segments[idx] && (segments[idx].status === 'starting' || segments[idx].status === 'executing')
          );
          if (toolIndex !== undefined && toolIndex < segments.length) {
            segments[toolIndex] = {
              ...segments[toolIndex],
              status: 'completed',
              result: data.result
            };
          }
        } catch (e) {
          console.error('Error parsing MCP_TOOL_RESULT:', e);
        }
      } else if (token.includes('MCP_TOOL_ERROR')) {
        try {
          const startIdx = token.indexOf('MCP_TOOL_ERROR') + 'MCP_TOOL_ERROR'.length;
          let jsonStr = token.substring(startIdx);
          // Skip any underscores after the token name
          jsonStr = jsonStr.replace(/^_+/, '');
          const data = JSON.parse(jsonStr);
          // Find the first MCP tool that's in 'starting' or 'executing' status
          const toolIndex = mcpToolIndexes.find(idx =>
            segments[idx] && (segments[idx].status === 'starting' || segments[idx].status === 'executing')
          );
          if (toolIndex !== undefined && toolIndex < segments.length) {
            segments[toolIndex] = {
              ...segments[toolIndex],
              status: 'error',
              error: data.error
            };
          }
        } catch (e) {
          console.error('Error parsing MCP_TOOL_ERROR:', e);
        }
      } else if (token.startsWith('__CHART_START__')) {
        // Save any accumulated content
        if (tempContent) {
          segments.push({ type: 'content', content: tempContent });
          tempContent = '';
        }

        try {
          // Extract chart data between __CHART_START__ and __CHART_END__
          const chartDataStr = token.replace('__CHART_START__', '').replace('__CHART_END__', '');
          const chartData = JSON.parse(chartDataStr);

          segments.push({
            type: 'chart',
            config: chartData.config,
            metadata: chartData.metadata
          });
        } catch (e) {
          console.error('Error parsing CHART data during streaming:', e);
        }
      } else if (token.startsWith('__COMPACTION_SUMMARY__')) {
        // Save any accumulated content
        if (tempContent) {
          segments.push({ type: 'content', content: tempContent });
          tempContent = '';
        }

        try {
          const compactionDataStr = token.replace('__COMPACTION_SUMMARY__', '');
          const compactionData = JSON.parse(compactionDataStr);

          segments.push({
            type: 'compaction_summary',
            ...compactionData
          });
        } catch (e) {
          console.error('Error parsing COMPACTION_SUMMARY during streaming:', e);
        }
      } else if (token === '__THINKING_START__') {
        // Save any accumulated content
        if (tempContent) {
          segments.push({ type: 'content', content: tempContent });
          tempContent = '';
        }
        inThinking = true;
        tempThinking = '';
        thinkingStartIndex = segments.length;
        // Add thinking segment immediately with stable initial state
        segments.push({ type: 'thinking', content: '', isActive: true });
      } else if (token === '__THINKING_END__') {
        if (thinkingStartIndex >= 0 && thinkingStartIndex < segments.length) {
          // Update the thinking segment with final content
          segments[thinkingStartIndex] = { 
            type: 'thinking', 
            content: tempThinking, 
            isActive: false 
          };
        }
        inThinking = false;
        tempThinking = '';
      } else if (token.startsWith('__THINKING__')) {
        const thinkingText = token.replace('__THINKING__', '');
        tempThinking += thinkingText;
        // Only update thinking segment periodically to reduce flickering (less frequent for performance)
        if (thinkingStartIndex >= 0 && thinkingStartIndex < segments.length && tempThinking.length % 50 === 0) {
          segments[thinkingStartIndex] = {
            type: 'thinking',
            content: tempThinking,
            isActive: true
          };
        }
      } else if (!token.startsWith('__') && !token.includes('MCP_TOOL_')) {
        // Regular content
        if (inThinking) {
          tempThinking += token;
          // Only update thinking segment periodically for regular content too (less frequent for performance)
          if (thinkingStartIndex >= 0 && thinkingStartIndex < segments.length && tempThinking.length % 100 === 0) {
            segments[thinkingStartIndex] = {
              type: 'thinking',
              content: tempThinking,
              isActive: true
            };
          }
        } else {
          // Regular content token
          if (inHtmlBlock) {
            // We're inside an HTML block, accumulate the content
            accumulatedHtml += token;
            
            // Check if we've reached the end of the HTML block
            if (accumulatedHtml.includes('```')) {
              // HTML block is complete
              const htmlEndIndex = accumulatedHtml.indexOf('```');
              const htmlContent = accumulatedHtml.substring(0, htmlEndIndex).trim();
              
              // Unescape HTML entities
              const unescapedContent = htmlContent
                .replace(/&lt;/g, '<')
                .replace(/&gt;/g, '>')
                .replace(/&amp;/g, '&')
                .replace(/&quot;/g, '"')
                .replace(/&#39;/g, "'");
              
              // Update or create the HTML segment
              if (htmlStartIndex >= 0 && htmlStartIndex < segments.length) {
                segments[htmlStartIndex] = {
                  type: 'html_content',
                  content: unescapedContent,
                  isPartial: false
                };
              }
              
              // Reset HTML tracking
              inHtmlBlock = false;
              htmlStartIndex = -1;
              
              // Handle any content after the closing ```
              tempContent = accumulatedHtml.substring(htmlEndIndex + 3);
              accumulatedHtml = '';
            } else {
              // Still accumulating HTML, update the segment with partial content
              if (htmlStartIndex >= 0 && htmlStartIndex < segments.length) {
                // Only update if we have substantial content
                if (accumulatedHtml.length > 50 || accumulatedHtml.includes('</')) {
                  const unescapedPartial = accumulatedHtml.trim()
                    .replace(/&lt;/g, '<')
                    .replace(/&gt;/g, '>')
                    .replace(/&amp;/g, '&')
                    .replace(/&quot;/g, '"')
                    .replace(/&#39;/g, "'");
                  
                  segments[htmlStartIndex] = {
                    type: 'html_content',
                    content: unescapedPartial,
                    isPartial: true
                  };
                }
              }
            }
          } else {
            // Not in HTML block, accumulate regular content
            tempContent += token;
            
            // Check if we're starting an HTML block
            const htmlStartMatch = tempContent.match(/```html\s*/);
            if (htmlStartMatch) {
              // Save any content before the HTML block
              const beforeHtml = tempContent.substring(0, htmlStartMatch.index).trim();
              if (beforeHtml) {
                segments.push({ type: 'content', content: beforeHtml });
              }
              
              // Start HTML block
              inHtmlBlock = true;
              htmlStartIndex = segments.length;
              accumulatedHtml = '';
              tempContent = '';
              
              // Add placeholder HTML segment
              segments.push({
                type: 'html_content',
                content: '',
                isPartial: true
              });
            }
          }
          
          // If we were in web search, mark it as complete while preserving data
          if (inWebSearch && webSearchStartIndex >= 0) {
            segments[webSearchStartIndex] = { 
              ...segments[webSearchStartIndex],
              status: 'complete' 
            };
            inWebSearch = false;
            setWebSearchActive(false);
          }
        }
      }
    });

    // Add any remaining content
    if (tempContent && tempContent.trim()) {
      segments.push({ type: 'content', content: tempContent.trim() });
    }
    
    // If we're still in an HTML block at the end (incomplete), make sure it's in the segments
    if (inHtmlBlock && htmlStartIndex >= 0 && accumulatedHtml) {
      const unescapedPartial = accumulatedHtml.trim()
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
      
      if (htmlStartIndex < segments.length) {
        segments[htmlStartIndex] = {
          type: 'html_content',
          content: unescapedPartial,
          isPartial: true
        };
      }
    }
    
    // Ensure final thinking content is captured even if not updated due to throttling
    if (inThinking && tempThinking && thinkingStartIndex >= 0 && thinkingStartIndex < segments.length) {
      segments[thinkingStartIndex] = {
        type: 'thinking',
        content: tempThinking,
        isActive: true
      };
    }

    setMessageSegments(segments);
  }, [content, streamingTokens, isStreaming]);

  // Force final update when streaming stops to ensure all content is captured
  useEffect(() => {
    if (!isStreaming && (content || streamingTokens.length > 0)) {
      // Small delay to ensure all state updates are complete
      const timeoutId = setTimeout(() => {
        processTokens();
      }, 50);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isStreaming]);

  useEffect(() => {
    // Clear existing timeout
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    // Handle immediate updates for special tokens
    const hasSpecialTokens = streamingTokens.some(token => 
      token.startsWith('__THINKING_START__') || 
      token.startsWith('__THINKING_END__') ||
      token.startsWith('__PROGRESS_START__') ||
      token.startsWith('__WEB_SEARCH_START__')
    );

    if (hasSpecialTokens) {
      // Process immediately for special tokens
      processTokens();
    } else {
      // Throttle regular content updates for better performance
      updateTimeoutRef.current = setTimeout(() => {
        processTokens();
      }, 200); // Update every 200ms to reduce load
    }

    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      if (htmlUpdateTimeoutRef.current) {
        clearTimeout(htmlUpdateTimeoutRef.current);
      }
    };
  }, [streamingTokens, isStreaming, content]);

  const renderSegment = (segment, index) => {
    switch (segment.type) {
      case 'content':
        // Remove any remaining MCP tokens from content before rendering
        // MCP tokens should already be extracted as separate segments, but sometimes
        // they slip through in the database content
        let cleanContent = segment.content;
        if (cleanContent && cleanContent.includes('MCP_TOOL_')) {
          // Remove all MCP token patterns (with or without underscores)
          // This regex matches: MCP_TOOL_TYPE followed by optional JSON
          const mcpPattern = /_*MCP_TOOL_(?:START|EXECUTING|RESULT|ERROR)_*/g;
          let lastIndex = 0;
          let result = '';
          let match;

          mcpPattern.lastIndex = 0;
          while ((match = mcpPattern.exec(cleanContent)) !== null) {
            // Add content before the match
            result += cleanContent.substring(lastIndex, match.index);

            // Check if there's JSON after the token
            const afterMatch = cleanContent.substring(match.index + match[0].length);
            if (afterMatch.startsWith('{')) {
              // Find end of JSON by counting braces
              let braceCount = 0;
              let jsonEnd = -1;
              for (let i = 0; i < afterMatch.length; i++) {
                if (afterMatch[i] === '{') braceCount++;
                if (afterMatch[i] === '}') braceCount--;
                if (braceCount === 0 && afterMatch[i] === '}') {
                  jsonEnd = i + 1;
                  break;
                }
              }
              // Skip the JSON content
              if (jsonEnd > 0) {
                lastIndex = match.index + match[0].length + jsonEnd;
                mcpPattern.lastIndex = lastIndex;
                continue;
              }
            }

            lastIndex = match.index + match[0].length;
          }

          // Add remaining content
          result += cleanContent.substring(lastIndex);
          cleanContent = result;
        }
        return (
          <div key={index}>
            <MessageRenderer content={cleanContent} />
          </div>
        );
      
      case 'html_content':
        return (
          <div key={index} className="my-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700">
              {/* HTML View Header */}
              <div className="flex items-center justify-between p-4 border-b bg-gray-50 dark:bg-gray-700 rounded-t-lg">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">HTML Report</h3>
                  <span className="text-xs text-gray-500">(Streaming)</span>
                </div>
                <div className="flex items-center gap-2">
                  {/* View Mode Switcher */}
                  <div className="flex items-center bg-gray-200 dark:bg-gray-600 rounded-lg p-0.5">
                    <button
                      onClick={() => setHtmlViewMode('preview')}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                        htmlViewMode === 'preview'
                          ? 'bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 shadow-sm'
                          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                      }`}
                    >
                      <Eye className="w-3 h-3" />
                      Preview
                    </button>
                    <button
                      onClick={() => setHtmlViewMode('code')}
                      className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                        htmlViewMode === 'code'
                          ? 'bg-white dark:bg-gray-800 text-purple-600 dark:text-purple-400 shadow-sm'
                          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100'
                      }`}
                    >
                      <Code className="w-3 h-3" />
                      Code
                    </button>
                  </div>
                  
                  {/* Fullscreen and Download buttons (only in preview mode) */}
                  {htmlViewMode === 'preview' && (
                    <>
                      <button
                        onClick={() => setIsFullscreen(!isFullscreen)}
                        className="p-1 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        title="Fullscreen"
                      >
                        <Maximize2 className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => {
                          const htmlContent = segment.content;
                          
                          // Create a new window with the HTML content for printing to PDF
                          const printWindow = window.open('', '_blank');
                          printWindow.document.write(`
                            <!DOCTYPE html>
                            <html>
                            <head>
                              <title>Report - ${new Date().toISOString().split('T')[0]}</title>
                              <style>
                                @media print {
                                  body { margin: 0; }
                                  @page { margin: 1in; }
                                }
                              </style>
                            </head>
                            <body>
                              ${htmlContent}
                            </body>
                            </html>
                          `);
                          printWindow.document.close();
                          
                          // Wait for content to load, then trigger print dialog
                          printWindow.onload = () => {
                            setTimeout(() => {
                              printWindow.print();
                              printWindow.close();
                            }, 500);
                          };
                        }}
                        className="p-1 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        title="Download as PDF"
                      >
                        <Download className="w-3 h-3" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              
              {/* HTML Content */}
              <div className={isFullscreen ? 'fixed inset-0 z-50 bg-white dark:bg-gray-800 overflow-auto' : 'max-h-[600px] overflow-hidden'}>
                {isFullscreen && (
                  <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 p-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold dark:text-gray-100">HTML Report - Fullscreen</h3>
                    <button
                      onClick={() => setIsFullscreen(false)}
                      className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
                    >
                      ✕
                    </button>
                  </div>
                )}
                
                {htmlViewMode === 'preview' ? (
                  <div className={isFullscreen ? 'p-6' : 'p-4'}>
                    <div className="relative w-full overflow-hidden" style={{ height: isFullscreen ? 'calc(100vh - 120px)' : '500px' }}>
                      <iframe
                        srcDoc={`
                          <!DOCTYPE html>
                          <html>
                          <head>
                            <meta charset="UTF-8">
                            <meta name="viewport" content="width=device-width, initial-scale=1.0">
                            <style>
                              body {
                                margin: 0;
                                padding: 16px;
                                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                                overflow-x: auto;
                                color: ${document.documentElement.classList.contains('dark') ? '#e5e7eb' : '#111827'};
                                background-color: ${document.documentElement.classList.contains('dark') ? '#1f2937' : '#ffffff'};
                              }
                              * { box-sizing: border-box; }

                              /* Table styling for both light and dark modes */
                              table {
                                width: 100%;
                                border-collapse: collapse;
                                margin: 16px 0;
                                background-color: ${document.documentElement.classList.contains('dark') ? '#374151' : '#ffffff'};
                                border: 1px solid ${document.documentElement.classList.contains('dark') ? '#4b5563' : '#e5e7eb'};
                                border-radius: 8px;
                                overflow: hidden;
                              }

                              th {
                                background-color: ${document.documentElement.classList.contains('dark') ? '#1f2937' : '#f9fafb'};
                                color: ${document.documentElement.classList.contains('dark') ? '#f3f4f6' : '#111827'};
                                font-weight: 600;
                                text-align: left;
                                padding: 12px;
                                border-bottom: 2px solid ${document.documentElement.classList.contains('dark') ? '#4b5563' : '#e5e7eb'};
                              }

                              td {
                                padding: 12px;
                                color: ${document.documentElement.classList.contains('dark') ? '#e5e7eb' : '#374151'};
                                border-bottom: 1px solid ${document.documentElement.classList.contains('dark') ? '#4b5563' : '#e5e7eb'};
                              }

                              tr:last-child td {
                                border-bottom: none;
                              }

                              tr:hover {
                                background-color: ${document.documentElement.classList.contains('dark') ? '#4b5563' : '#f3f4f6'};
                              }

                              /* Links in tables */
                              table a {
                                color: ${document.documentElement.classList.contains('dark') ? '#60a5fa' : '#2563eb'};
                                text-decoration: none;
                              }

                              table a:hover {
                                text-decoration: underline;
                              }

                              /* General text color for other elements */
                              h1, h2, h3, h4, h5, h6 {
                                color: ${document.documentElement.classList.contains('dark') ? '#f3f4f6' : '#111827'};
                              }

                              p, div, span, li {
                                color: ${document.documentElement.classList.contains('dark') ? '#e5e7eb' : '#374151'};
                              }

                              /* Code blocks */
                              code, pre {
                                background-color: ${document.documentElement.classList.contains('dark') ? '#1f2937' : '#f3f4f6'};
                                color: ${document.documentElement.classList.contains('dark') ? '#fbbf24' : '#dc2626'};
                                padding: 2px 4px;
                                border-radius: 4px;
                              }
                            </style>
                          </head>
                          <body>
                            ${segment.content}
                          </body>
                          </html>
                        `}
                        className="absolute inset-0 w-full h-full border-0 bg-white dark:bg-gray-800 rounded"
                        title="HTML Preview"
                        sandbox="allow-scripts"
                        style={{ 
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          isolation: 'isolate'
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">HTML Source</span>
                      <button
                        onClick={() => navigator.clipboard.writeText(segment.content)}
                        className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700 font-medium"
                      >
                        <Copy className="w-3 h-3" />
                        Copy
                      </button>
                    </div>
                    <pre className="bg-gray-900 text-green-400 p-3 rounded text-xs overflow-auto max-h-[500px] font-mono">
                      <code>{segment.content}</code>
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      
      case 'web_search':
        return (
          <div key={index} className="my-4">
            <WebSearchProgress 
              status={segment.status} 
              query={segment.query || webSearchQuery}
              results={segment.results || []}
            />
          </div>
        );

      case 'thinking':
        return (
          <div key={index} className="my-4">
            <ThinkingDisplay
              content={segment.content}
              isActive={segment.isActive || false}
              model={model}
              isStreaming={isStreaming}
            />
          </div>
        );

      case 'mcp_tool':
        // Determine if this is the latest MCP tool (for auto-expand during streaming)
        // After streaming completes, all tools should be collapsed by default
        const isLatestTool = messageSegments
          .slice(index + 1)
          .every(seg => seg.type !== 'mcp_tool');

        return (
          <div key={index} className="my-2">
            <MCPToolProgress
              toolName={segment.toolName}
              serverName={segment.serverName}
              status={segment.status}
              input={segment.input}
              result={segment.result}
              error={segment.error}
              isLatest={isStreaming && isLatestTool}
            />
          </div>
        );

      case 'chart':
        return (
          <div key={index} className="my-4">
            <ChartDisplay
              config={segment.config}
              metadata={segment.metadata}
            />
          </div>
        );

      case 'compaction_summary':
        return (
          <div key={index} className="my-3">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <div className="text-purple-600 mt-0.5">
                  💾
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-purple-900 mb-1">
                    Context Optimized - Conversation Summarized
                  </div>
                  <div className="text-xs text-purple-700 space-y-0.5">
                    <div>Compacted {segment.originalMessages} messages → {segment.compactedMessages} messages</div>
                    <div>Saved {segment.tokensSaved} tokens ({segment.reductionPercent}% reduction)</div>
                    <div className="text-purple-600 mt-1">
                      ✓ Using compacted conversation history for better performance
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Progress display component
  const ProgressDisplay = ({ status, text }) => {
    if (!status || !text) return null;

    const getProgressConfig = (status) => {
      switch (status) {
        case 'starting':
          return {
            icon: Bot,
            bgColor: 'bg-blue-50',
            iconColor: 'text-blue-600',
            textColor: 'text-blue-800',
            borderColor: 'border-blue-200'
          };
        case 'tool_auto':
          return {
            icon: '🔧',
            bgColor: 'bg-purple-50',
            iconColor: 'text-purple-600',
            textColor: 'text-purple-800',
            borderColor: 'border-purple-200'
          };
        case 'streaming':
          return {
            icon: Loader2,
            bgColor: 'bg-green-50',
            iconColor: 'text-green-600',
            textColor: 'text-green-800',
            borderColor: 'border-green-200'
          };
        case 'tools_ready':
          return {
            icon: '🛠️',
            bgColor: 'bg-orange-50',
            iconColor: 'text-orange-600',
            textColor: 'text-orange-800',
            borderColor: 'border-orange-200'
          };
        default:
          return {
            icon: Bot,
            bgColor: 'bg-gray-50',
            iconColor: 'text-gray-600',
            textColor: 'text-gray-800',
            borderColor: 'border-gray-200'
          };
      }
    };

    const config = getProgressConfig(status);
    const Icon = typeof config.icon === 'string' ? () => <span>{config.icon}</span> : config.icon;

    return (
      <div className={`flex items-center gap-3 p-3 mb-3 rounded-lg border ${config.bgColor} ${config.borderColor} transition-all duration-300`}>
        <Icon 
          className={`w-4 h-4 ${config.iconColor} ${status === 'streaming' ? 'animate-spin' : ''}`} 
        />
        <span className={`text-sm font-medium ${config.textColor}`}>
          {text}
        </span>
      </div>
    );
  };

  return (
    <div className="streaming-message-content">
      {progressInfo && (
        <ProgressDisplay status={progressInfo.status} text={progressInfo.text} />
      )}
      {messageSegments.map((segment, index) => renderSegment(segment, index))}
      {isStreaming && messageSegments.length === 0 && !progressInfo && !streamingTokens.some(token => !token.startsWith('__')) && (
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Generating response...</span>
        </div>
      )}
    </div>
  );
});

export default StreamingMessageRenderer;